import type { AppDirectClient } from '../client.js';
import { AuthError } from '../errors.js';
import {
  buildSessionCookies,
  COOKIE_OAUTH_STATE,
} from '../session/cookies.js';
import type { CallbackResult, CookieStore, ResolvedConfig } from '../types.js';
import { assertConfiguredApplicationAssignment } from './entitlement.js';

export async function handleCallback(
  config: ResolvedConfig,
  client: AppDirectClient,
  input: {
    code?: string;
    error?: string;
    state?: string;
    cookies: CookieStore;
  },
): Promise<CallbackResult> {
  if (input.error || !input.code) {
    throw new AuthError('no_code', input.error ?? 'Authorization code missing');
  }

  const storedState = input.cookies.get(COOKIE_OAUTH_STATE);
  if (!storedState || !input.state || storedState !== input.state) {
    throw new AuthError('invalid_state', 'OAuth state mismatch');
  }

  const oauthTokens = await client.exchangeAuthorizationCode(input.code);
  const authTokens = await client.exchangeAuthToken(oauthTokens.access_token);
  const user = await client.resolveUser(oauthTokens.access_token, oauthTokens.id_token);

  await assertConfiguredApplicationAssignment(config, client, authTokens.accessToken, user);

  const refreshToken = authTokens.refreshToken ?? null;

  return {
    fejwt: authTokens.accessToken,
    refreshToken,
    user,
    cookiesToSet: buildSessionCookies(config, authTokens.accessToken, refreshToken),
    cookiesToClear: [COOKIE_OAUTH_STATE],
  };
}
